import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// One set of student stages used by both the Pipeline board and the Students list.
export const STAGES = [
  { key: 'leads', title: 'Leads', tone: 'slate' },
  { key: 'registrations', title: 'Registered', tone: 'blue' },
  { key: 'enrolled', title: 'Enrolled', tone: 'pink' },
  { key: 'completed', title: 'Completed', tone: 'green' },
  { key: 'drop_offs', title: 'Dropped out', tone: 'red' },
];
export const STAGE_TITLE = Object.fromEntries(STAGES.map(s => [s.key, s.title]));
const DROP_REASONS = ['Not interested', 'No-show', 'Failed eligibility / test', "Can't afford", 'Went elsewhere', 'Uncontactable', 'Withdrew from course', 'Other'];

const explain = (from, to, name) => {
  if (to === 'enrolled' && from === 'completed') return `Re-open ${name}'s completed course and put them back on it.`;
  if (to === 'enrolled') return `Enrol ${name} on a course. They'll count as a student and get the referral invite (if switched on).`;
  if (to === 'completed') return `Mark ${name}'s course as complete. This also sends their Google review request.`;
  if (to === 'drop_offs') return from === 'enrolled' || from === 'completed'
    ? `Withdraw ${name} from their course and mark them as dropped out.`
    : `Mark ${name} as dropped out.`;
  if (to === 'registrations') return `Move ${name} to Registered (no appointment is booked — use Add Registration for that).`;
  return `Move ${name} back to Leads.`;
};

/** requestMove(card, fromStage, toStage) opens a confirmation; the move only happens when confirmed. */
export const usePipelineMove = (onMoved) => {
  const [move, setMove] = useState(null);
  const [courses, setCourses] = useState([]);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState(null);

  const requestMove = (card, from, to) => {
    if (from === to) return;
    if ((to === 'leads' || to === 'registrations') && (from === 'enrolled' || from === 'completed')) {
      toast.error(`${card.name} is on a course. Mark them as dropped out first to withdraw them.`);
      return;
    }
    if (to === 'completed' && from !== 'enrolled') {
      toast.error('Only students currently on a course can be marked completed.');
      return;
    }
    setError(null);
    setMove({ card, from, to, course_id: "", reason: "", note: "" });
    if (to === 'enrolled' && from !== 'completed') {
      fetch(`${API_URL}/api/training/courses`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : []))
        .then(list => setCourses((list || []).filter(c => c.status !== 'completed')))
        .catch(() => setCourses([]));
    }
  };

  const confirm = async () => {
    if (move.to === 'enrolled' && move.from !== 'completed' && !move.course_id) { setError('Choose a course first'); return; }
    if (move.to === 'drop_offs' && !move.reason) { setError('Choose a reason first'); return; }
    setMoving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/pipeline/move`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: move.card.lead_id, to: move.to,
          course_id: move.course_id || undefined, reason: move.reason || undefined, note: move.note || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${move.card.name}: ${body.summary || `moved to ${STAGE_TITLE[move.to]}`}`);
        setMove(null);
        onMoved?.();
      } else {
        setError(typeof body.detail === "string" ? body.detail : "Could not move this student");
      }
    } finally {
      setMoving(false);
    }
  };

  const moveDialog = (
    <Dialog open={!!move} onOpenChange={(o) => { if (!o && !moving) setMove(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Move to {move ? STAGE_TITLE[move.to] : ''}?</DialogTitle>
        </DialogHeader>
        {move && (
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600">{explain(move.from, move.to, move.card.name)}</p>
            {move.to === 'enrolled' && move.from !== 'completed' && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Course</label>
                <Select value={move.course_id} onValueChange={(v) => { setError(null); setMove(m => ({ ...m, course_id: v })); }}>
                  <SelectTrigger className="rounded-xl h-10"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map(c => {
                      const full = c.capacity && (c.enrolled_count || 0) >= c.capacity;
                      return (
                        <SelectItem key={c.course_id} value={c.course_id} disabled={full}>
                          {c.name}{c.capacity ? ` (${c.enrolled_count || 0}/${c.capacity})` : ""}{full ? " · Full" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            {move.to === 'drop_offs' && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Reason</label>
                  <Select value={move.reason} onValueChange={(v) => setMove(m => ({ ...m, reason: v }))}>
                    <SelectTrigger className="rounded-xl h-10"><SelectValue placeholder="Why did they drop out?" /></SelectTrigger>
                    <SelectContent>{DROP_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input value={move.note} onChange={(e) => setMove(m => ({ ...m, note: e.target.value }))} placeholder="Note (optional)" maxLength={300} className="rounded-xl" />
              </>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => setMove(null)} disabled={moving}>Cancel</Button>
          <Button className="btn-primary rounded-xl" onClick={confirm} disabled={moving}>
            {moving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Yes, move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { requestMove, moveDialog };
};

/** Small "Move to…" dropdown for places where drag-and-drop isn't available (phones, lists). */
export const MoveToSelect = ({ card, from, onMove }) => (
  <select
    aria-label={`Move ${card.name}`}
    value=""
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => { if (e.target.value) onMove(card, from, e.target.value); }}
    className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-md px-1 py-0.5 hover:border-slate-300"
  >
    <option value="">Move to…</option>
    {STAGES.filter(s => s.key !== from).map(s => <option key={s.key} value={s.key}>{s.title}</option>)}
  </select>
);
